import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useIsMount } from 'hooks/useIsMountHook';
import _ from 'lodash';
import i18n from 'i18n';
import { Move } from './DraggableTransactionsPriorityTable';
import { Button } from 'react-bootstrap';
import { RecordingMessage } from 'types/rest/assetCatalogTypes';
import { getSelectedCountTitle, moveElementsToEnd, moveElementToBeginning } from 'utils/miscUtil';
import BzmIcon from 'components/icons/BzmIcon';
import { useDispatch } from 'react-redux';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import SimpleLoader from 'components/loaders/SimpleLoader';
import { getRecordingMessagesForIndexModal } from '../../../../../store/recordings/recordingMessages/recordingMessagesActions';
import { RecordingMessageIndex } from '../../../../../store/recordings/recordingMessages/recordingMessagesTypes';
import { updatePatchRecording } from '../../../../../store/recordings/recordingsActions';
import DraggableMessagesIndexTable from './DraggableMessagesIndexTable';

interface MessagesIndexModalProps {
  recordingId: number;
  recordingName: string;
  onHide: () => void;
}
const MessagesIndexModal = (props: MessagesIndexModalProps) => {
  const isMount = useIsMount();
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const dispatch = useDispatch();

  const getMessagesForEntity = async () => {
    setMessages(await getRecordingMessagesForIndexModal(props.recordingId));
  };

  //On mount
  useEffect(() => {
    if (isMount) {
      getMessagesForEntity().then(() => {
        setIsLoading(false);
      });
    }
  }, []);

  const getCounterValue = useMemo(() => {
    return getSelectedCountTitle(_.size(messages), _.size(selected));
  }, [selected]);

  const saveIndexesHandler = async () => {
    setIsSaving(true);
    const msgIdIndex = _.map(messages, msg => {
      return { id: msg.id, index: msg.index } as RecordingMessageIndex;
    });
    const msPatch = { messages: msgIdIndex };
    await dispatch(updatePatchRecording(props.recordingId, msPatch, props.recordingName));
    props.onHide();
  };

  /**
   * Reassigns index of messages based on the order in the array passed.
   * @param items
   */
  const recalculateIndex = (items: RecordingMessage[]) => {
    let counter = 1;
    const recalculated = _.map(items, (item: RecordingMessage) => {
      return { ...item, index: counter++ };
    });
    return recalculated;
  };

  const showSuccessMessage = () => {
    dispatch(showToastNotification(ToastType.Success, 'Message index has been successfully updated.'));
  };

  const moveMessageHandler = (message: RecordingMessage, moveType: Move) => {
    let updatedMsgs = _.cloneDeep(messages);
    if (moveType === Move.start) {
      updatedMsgs = moveElementToBeginning(updatedMsgs, [message] as any);
    } else {
      updatedMsgs = moveElementsToEnd(updatedMsgs, [message] as any[]);
    }
    updatedMsgs = recalculateIndex(updatedMsgs);
    setMessages(updatedMsgs);
    //showSuccessMessage();
  };

  /**
   * Map selected message ids to messages.
   */
  const getSelectedMessages = () => {
    return _.map(selected, element => {
      return messages.find(txn => txn.id === element);
    });
  };

  /**
   * Bulk move of multiple messages to beginning / end.
   * @param moveType
   */
  const moveSelectedMessagesInBulk = (moveType: Move) => {
    const toMove = getSelectedMessages();
    let updated: any[];
    if (moveType === Move.start) {
      updated = moveElementToBeginning(messages, toMove);
    } else {
      updated = moveElementsToEnd(messages, toMove);
    }
    updated = recalculateIndex(updated);
    setMessages(updated);
    setSelected([]);
    //showSuccessMessage();
  };

  const getActionButtons = () => {
    const txnCount = _.size(messages);
    const selectedCount = _.size(selected);
    const actionDisabled = selectedCount === 0 || txnCount === selectedCount;
    return (
      <div className={'d-inline-block ml-5'}>
        <Button variant={'link'} className={'btn-no-hover p-0'} disabled={actionDisabled}>
          <BzmIcon
            size={16}
            className={'hand-cursor'}
            variant={'icon-bzm-move-up'}
            onClick={() => moveSelectedMessagesInBulk(Move.start)}
          />
        </Button>
        <Button variant={'link'} className={'btn-no-hover p-0'} disabled={actionDisabled}>
          <BzmIcon
            size={16}
            className={'hand-cursor pl-2'}
            variant={'icon-bzm-move-down'}
            onClick={() => moveSelectedMessagesInBulk(Move.end)}
          />
        </Button>
      </div>
    );
  };

  return (
    <Modal
      animation={false}
      show={true}
      backdrop={true}
      onHide={props.onHide}
      size="xl"
      dialogClassName="modal-full-screen"
    >
      <Modal.Header closeButton>
        <h4 className="modal-title">{i18n.t('recordings:dragAndDrop.title')}</h4>
      </Modal.Header>
      <Modal.Body>
        <p>{i18n.t('recordings:dragAndDrop.description')}</p>
        <h4>
          Messages {getCounterValue}
          {getActionButtons()}
        </h4>
        <DraggableMessagesIndexTable
          messages={messages}
          isLoading={isLoading}
          onSelectionChange={(selectedItems: any[]) => {
            setSelected(selectedItems);
          }}
          moveMessage={moveMessageHandler}
          onDragFinished={(items: RecordingMessage[]) => {
            setMessages(recalculateIndex(items));
            //showSuccessMessage();
          }}
          selectedItems={selected}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={props.onHide}>
          {i18n.t('common:actions.cancel')}
        </Button>
        <Button onClick={saveIndexesHandler} disabled={isSaving}>
          {isSaving && <SimpleLoader className={'d-inline-block mr-3'} showLoadingText={false} />}
          {i18n.t('common:actions.save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MessagesIndexModal;
